// src/pages/CommunityPage.jsx
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar // Added Snackbar import
} from '@mui/material';
import {
  TrendingUp,
  Forum,
  Groups,
  Event,
  QuestionAnswer,
  Search,
  ThumbUp,
  Comment,
  Visibility,
  Add,
  Whatshot,
  NewReleases,
  Star,
  CheckCircle, 
  Close
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// Mock data for community content
const mockPosts = [
  {
    id: 1,
    title: 'Best practices for property photography?',
    content: 'I\'m new to property photography and would love to hear some tips from experienced members. What equipment do you recommend?',
    author: { name: 'Sarah Johnson', avatar: '', rating: 4.8 },
    category: 'Photography',
    likes: 24,
    comments: 12,
    views: 156,
    timestamp: '2 hours ago',
    isTrending: true,
    isNew: true
  },
  {
    id: 2,
    title: 'How to handle difficult property verifications?',
    content: 'Recently encountered a property where the owner was reluctant to allow verification. Any advice on handling such situations?',
    author: { name: 'Mike Chen', avatar: '', rating: 4.5 },
    category: 'Verification',
    likes: 18,
    comments: 8,
    views: 98,
    timestamp: '5 hours ago',
    isTrending: true,
    isNew: false
  },
  {
    id: 3,
    title: 'Earning potential in different cities?',
    content: 'Has anyone compared task availability and rates across different UK cities? Share your experiences!',
    author: { name: 'Emma Davis', avatar: '', rating: 4.9 },
    category: 'General',
    likes: 32,
    comments: 15,
    views: 210,
    timestamp: '1 day ago',
    isTrending: false,
    isNew: false
  }
];

const mockTopics = [
  { id: 1, name: 'Photography', count: 124, icon: <Add /> },
  { id: 2, name: 'Verification', count: 89, icon: <QuestionAnswer /> },
  { id: 3, name: 'Research', count: 67, icon: <Search /> },
  { id: 4, name: 'Virtual Tours', count: 45, icon: <Visibility /> },
  { id: 5, name: 'Pricing', count: 78, icon: <TrendingUp /> },
  { id: 6, name: 'Tools & Equipment', count: 56, icon: <Star /> }
];

const mockTopContributors = [
  { id: 1, name: 'Alex Morgan', points: 1245, tasks: 89, rating: 4.9 },
  { id: 2, name: 'Jessica Lee', points: 987, tasks: 76, rating: 4.8 },
  { id: 3, name: 'David Wilson', points: 876, tasks: 68, rating: 4.7 },
  { id: 4, name: 'Maria Garcia', points: 765, tasks: 59, rating: 4.9 },
  { id: 5, name: 'James Brown', points: 654, tasks: 52, rating: 4.6 }
];

export default function CommunityPage() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [posts, setPosts] = useState(mockPosts);
  const [topics, setTopics] = useState(mockTopics);
  const [contributors, setContributors] = useState(mockTopContributors);
  
  // Snackbar notification state
  const [notification, setNotification] = useState({
    open: false,
    message: ''
  });

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    // In real implementation, this would filter posts
  };

  // Handle community action notifications
  const handleCommunityAction = () => {
    setNotification({
      open: true,
      message: 'You must verify your profile and complete basic level tasks to access and join our community!'
    });
  };

  // Fetch community data (would be API calls in real implementation)
  useEffect(() => {
    setLoading(true);
    // Simulate API loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      Photography: 'primary',
      Verification: 'success',
      Research: 'info',
      'Virtual Tours': 'warning',
      General: 'default',
      Pricing: 'secondary',
      'Tools & Equipment': 'error'
    };
    return colors[category] || 'default';
  };

  return (
    <DashboardLayout>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Community Hub
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Connect with other taskers, share tips, and get help from the community
        </Typography>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Main Content (8 columns on desktop) */}
        <Grid item xs={12} lg={8}>
          {/* Search and Create Post Bar */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  placeholder="Search discussions, topics, or members..."
                  value={searchQuery}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Add />}
                  onClick={handleCommunityAction} // Updated to show notification
                >
                  New Post
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Community Tabs */}
          <Paper>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<Forum />} label="All Discussions" />
              <Tab icon={<Whatshot />} label="Trending" />
              <Tab icon={<NewReleases />} label="New" />
              <Tab icon={<QuestionAnswer />} label="Questions" />
              <Tab icon={<Groups />} label="My Discussions" />
            </Tabs>

            {/* All Discussions Tab */}
            <TabPanel value={tabValue} index={0}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box>
                  {posts.map((post) => (
                    <Card key={post.id} sx={{ mb: 2, '&:hover': { boxShadow: 3 } }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {post.author.name[0]}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                              <Typography variant="h6" component="h2">
                                {post.title}
                              </Typography>
                              {post.isTrending && (
                                <Chip icon={<Whatshot />} label="Trending" color="warning" size="small" />
                              )}
                              {post.isNew && (
                                <Chip icon={<NewReleases />} label="New" color="success" size="small" />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              By {post.author.name} • {post.timestamp}
                            </Typography>
                          </Box>
                          <Chip 
                            label={post.category} 
                            color={getCategoryColor(post.category)} 
                            size="small" 
                          />
                        </Box>

                        <Typography variant="body1" paragraph>
                          {post.content}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ThumbUp fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {post.likes}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Comment fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {post.comments} comments
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Visibility fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {post.views}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          startIcon={<ThumbUp />}
                          onClick={handleCommunityAction} // Updated to show notification
                        >
                          Like
                        </Button>
                        <Button 
                          size="small" 
                          startIcon={<Comment />}
                          onClick={handleCommunityAction} // Updated to show notification
                        >
                          Comment
                        </Button>
                        <Button 
                          size="small"
                          onClick={handleCommunityAction} // Updated to show notification
                        >
                          Share
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                </Box>
              )}
            </TabPanel>

            {/* Other tabs would be implemented similarly */}
            <TabPanel value={tabValue} index={1}>
              <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                Trending discussions will appear here
              </Typography>
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                New discussions will appear here
              </Typography>
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                Questions from the community will appear here
              </Typography>
            </TabPanel>
            <TabPanel value={tabValue} index={4}>
              <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                Your discussions will appear here
              </Typography>
            </TabPanel>
          </Paper>
        </Grid>

        {/* Right Column - Sidebar (4 columns on desktop) */}
        <Grid item xs={12} lg={4}>
          {/* Popular Topics Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ mr: 1 }} />
                Popular Topics
              </Typography>
              <List dense>
                {topics.map((topic) => (
                  <ListItem key={topic.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                        {topic.icon}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={topic.name} 
                      secondary={`${topic.count} discussions`} 
                    />
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={handleCommunityAction} // Updated to show notification
                    >
                      Follow
                    </Button>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Top Contributors Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Star sx={{ mr: 1 }} />
                Top Contributors
              </Typography>
              <List dense>
                {contributors.map((contributor, index) => (
                  <ListItem key={contributor.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.main' }}>
                        {contributor.name[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={contributor.name} 
                      secondary={`${contributor.tasks} tasks • ⭐ ${contributor.rating}`} 
                    />
                    <Chip 
                      label={`#${index + 1}`} 
                      color={index === 0 ? 'warning' : 'default'} 
                      size="small" 
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Community Stats Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Groups sx={{ mr: 1 }} />
                Community Stats
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
                    <Typography variant="h4">1.2K</Typography>
                    <Typography variant="body2">Members</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.main', color: 'white' }}>
                    <Typography variant="h4">543</Typography>
                    <Typography variant="body2">Discussions</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.main', color: 'white' }}>
                    <Typography variant="h4">2.4K</Typography>
                    <Typography variant="body2">Comments</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.main', color: 'white' }}>
                    <Typography variant="h4">89</Typography>
                    <Typography variant="body2">Online Now</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Upcoming Events Card */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Event sx={{ mr: 1 }} />
                Upcoming Events
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Community meetup - London, Nov 25th
              </Alert>
              <Alert severity="info">
                Photography workshop - Online, Jan 21st
              </Alert>
              <Button 
                fullWidth 
                variant="outlined" 
                sx={{ mt: 2 }}
                onClick={handleCommunityAction} // Updated to show notification
              >
                View All Events
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    {/* Green-Themed Square Notification */}
          <Snackbar
            open={notification.open}
            autoHideDuration={6000}
            onClose={() => setNotification({ open: false, message: '' })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{
              '& .MuiSnackbar-root': {
                alignItems: 'center',
                justifyContent: 'center',
              }
            }}
          >
            {/* Main Square Notification Card - Green Theme */}
            <Paper
              sx={{
                width: '320px',
                height: '320px', // Perfect square
                borderRadius: 3,
                background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 50%, #1B5E20 100%)',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(76, 175, 80, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 3,
                border: '1px solid rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)'
              }}
            >
              {/* Green Theme Background Elements */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `
                    radial-gradient(circle at 20% 20%, rgba(76, 175, 80, 0.3) 0%, transparent 50%),
                    radial-gradient(circle at 80% 80%, rgba(46, 125, 50, 0.3) 0%, transparent 50%),
                    radial-gradient(circle at 40% 60%, rgba(27, 94, 32, 0.2) 0%, transparent 50%)
                  `,
                  zIndex: 0
                }}
              />
              
              {/* Floating Green Particles */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '20%',
                  left: '15%',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  animation: 'float 3s ease-in-out infinite',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-10px)' }
                  }
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '60%',
                  right: '20%',
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.6)',
                  animation: 'float 4s ease-in-out infinite 0.5s',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '30%',
                  left: '25%',
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.4)',
                  animation: 'float 3.5s ease-in-out infinite 1s',
                }}
              />

              {/* Close Button */}
              <IconButton
                onClick={() => setNotification({ open: false, message: '' })}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  width: 32,
                  height: 32,
                  zIndex: 2,
                  border: '1px solid rgba(255,255,255,0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <Close />
              </IconButton>

              {/* Success Icon Section */}
              <Box sx={{ 
                position: 'relative', 
                zIndex: 1,
                mt: 4,
                textAlign: 'center'
              }}>
                {/* Animated Checkmark Circle */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    boxShadow: '0 8px 24px rgba(27, 94, 32, 0.4)',
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.05)' }
                    },
                    border: '2px solid rgba(76, 175, 80, 0.3)'
                  }}
                >
                  <CheckCircle 
                    sx={{ 
                      fontSize: 42,
                      color: '#2E7D32', // Dark green to match theme
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                    }} 
                  />
                </Box>
                
                {/* Title with Green Text Shadow */}
                <Typography 
                  variant="h5" 
                  component="div"
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.6rem',
                    textShadow: '0 2px 8px rgba(27, 94, 32, 0.6)',
                    mb: 1,
                    letterSpacing: '0.5px'
                  }}
                >
                  Access Required!
                </Typography>
                
                {/* Subtitle */}
                <Typography 
                  variant="body2"
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    fontWeight: 500,
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                  }}
                >
                  Community features are locked
                </Typography>
              </Box>

              {/* Message Content */}
              <Box sx={{ 
                position: 'relative', 
                zIndex: 1,
                textAlign: 'center',
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2
              }}>
                <Typography 
                  variant="body1"
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    lineHeight: 1.5,
                    textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                    fontSize: '0.95rem',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    backdropFilter: 'blur(5px)'
                  }}
                >
                  {notification.message}
                </Typography>
              </Box>

              {/* Action Section */}
              <Box sx={{ 
                position: 'relative', 
                zIndex: 1,
                width: '100%',
                textAlign: 'center'
              }}>
                {/* Action Button */}
                <Button
                  onClick={() => setNotification({ open: false, message: '' })}
                  variant="contained"
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    color: '#2E7D32',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    px: 4,
                    py: 1.2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 4px 12px rgba(27, 94, 32, 0.4)',
                    border: '1px solid rgba(255,255,255,0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(27, 94, 32, 0.6)'
                    },
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  Okay!
                </Button>
                
                {/* Green Auto-close Timer Bar */}
                <Box 
                  sx={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    mt: 2,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      borderRadius: 2,
                      animation: 'shrink 6s linear forwards',
                      '@keyframes shrink': {
                        '0%': { transform: 'scaleX(1)' },
                        '100%': { transform: 'scaleX(0)' }
                      },
                      boxShadow: '0 0 8px rgba(255,255,255,0.5)'
                    }}
                  />
                </Box>
              </Box>

              {/* Decorative Corner Accents */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                  borderTopLeftRadius: 3
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(315deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                  borderBottomRightRadius: 3
                }}
              />
            </Paper>
          </Snackbar>
    </DashboardLayout>
  );
}